import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import netatmoFirestoreService from '../services/netatmoFirestoreService';
import netatmoFirebaseService from '../services/netatmoFirebaseService';
import PageShell, { Btn } from '../components/design/PageShell';

const Rain = () => {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState('all');
  const [period, setPeriod] = useState('week'); // week, month, year
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [stationData, setStationData] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const unsubAcc = netatmoFirestoreService.subscribeToAccounts((list) => {
      setAccounts(list.filter(a => a.isActive && !a.needsReauth));
    });
    return () => {
      unsubAcc && unsubAcc();
      netatmoFirestoreService.cleanup();
    };
  }, []);

  // Trigger background refresh via Firebase Function (populates the cache)
  const triggerBackgroundRefresh = useCallback(async (accs) => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    try {
      const list = accs && accs.length ? accs : accounts;
      for (let i = 0; i < list.length; i++) {
        if (i > 0) await new Promise(r => setTimeout(r, 1000));
        try {
          await netatmoFirebaseService.getRainData(list[i].id, period, {
            selectedYear: period === 'year' ? selectedYear : undefined,
            forceRefresh: true,
          });
        } catch (e) { console.warn('Refresh error', list[i].id, e?.message); }
      }
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  }, [accounts, period, selectedYear]);

  useEffect(() => {
    if (accounts.length === 0) return;
    setLoading(true);
    setStationData({});
    const unsubs = [];
    const bucket = {};
    let receivedCount = 0;
    let needsRefresh = false;
    const opts = period === 'year' ? { selectedYear } : {};

    accounts.forEach((acc) => {
      const unsub = netatmoFirestoreService.subscribeToRainData(acc.id, period, opts, (res) => {
        receivedCount++;
        if (res && res.success && res.data) {
          const formatted = netatmoFirestoreService.formatDataForPeriod(res.data, period);
          const summary = res.data.summary || formatted.summary || {};
          bucket[acc.id] = { account: acc, formatted, summary };
          setStationData({ ...bucket });
          setLoading(false);
          if (res.stale) needsRefresh = true;
        } else {
          // empty cache → trigger fetch
          bucket[acc.id] = { account: acc, formatted: { chartData: [], tableData: [], summary: {} }, summary: {} };
          setStationData({ ...bucket });
          setLoading(false);
          needsRefresh = true;
        }

        // Once we got at least one callback per station, decide if we need to refresh
        if (receivedCount >= accounts.length && needsRefresh) {
          triggerBackgroundRefresh(accounts);
          needsRefresh = false;
        }
      });
      unsubs.push(unsub);
    });

    // Safety net: if no callback after 2s, force a refresh
    const safety = setTimeout(() => {
      if (Object.keys(bucket).length === 0) {
        triggerBackgroundRefresh(accounts);
      }
    }, 2500);

    return () => {
      clearTimeout(safety);
      unsubs.forEach(u => u && u());
    };
  }, [accounts, period, selectedYear, triggerBackgroundRefresh]);

  const visibleStations = useMemo(() => {
    const keys = Object.keys(stationData);
    if (selected === 'all') return keys.map(k => stationData[k]);
    const found = stationData[selected];
    return found ? [found] : [];
  }, [stationData, selected]);

  const computeSummary = (s) => {
    const chart = s.formatted?.chartData || [];
    const total = chart.reduce((a, c) => a + (c.value || 0), 0);
    const max = chart.reduce((a, c) => Math.max(a, c.value || 0), 0);
    const rainyDays = chart.filter(c => (c.value || 0) > 0).length;
    const last = chart.length > 0 ? chart[chart.length - 1].value : 0;
    return {
      total: s.summary?.total ?? total,
      max: s.summary?.maximum ?? max,
      rainyDays: s.summary?.daysWithRain ?? rainyDays,
      last24: s.summary?.last24h ?? last,
      average: s.summary?.average ?? (chart.length > 0 ? total / chart.length : 0),
    };
  };

  const totalSum = visibleStations.reduce((a, s) => a + computeSummary(s).total, 0);
  const rainyDays = visibleStations.reduce((a, s) => Math.max(a, computeSummary(s).rainyDays), 0);

  const combinedChart = useMemo(() => {
    if (visibleStations.length === 0) return [];
    const lengths = visibleStations.map(s => (s.formatted?.chartData || []).length);
    const maxLen = Math.max(0, ...lengths);
    const result = [];
    for (let i = 0; i < maxLen; i++) {
      result.push({
        label: visibleStations[0]?.formatted?.chartData?.[i]?.name || '',
        values: visibleStations.map(s => ({
          account: s.account,
          v: s.formatted?.chartData?.[i]?.value ?? 0,
        })),
      });
    }
    return result;
  }, [visibleStations]);

  const maxBarValue = combinedChart.reduce(
    (m, c) => Math.max(m, ...c.values.map(v => v.v)),
    0.1
  );

  const colorFor = (i) => ['var(--accent)', 'var(--ok)', 'var(--warn)'][i % 3];

  const yearOptions = (() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2, y - 3];
  })();

  return (
    <PageShell
      section="pluie"
      number="07"
      title="Pluviométrie"
      subtitle={`${accounts.length} station${accounts.length > 1 ? 's' : ''} Netatmo · cumul et historique`}
      actions={
        <>
          {['week', 'month', 'year'].map(p => (
            <Btn key={p} kind={period === p ? 'accent' : 'ghost'} onClick={() => setPeriod(p)}>
              {p === 'week' ? 'Semaine' : p === 'month' ? 'Mois' : 'Année'}
            </Btn>
          ))}
          {period === 'year' && (
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                background: 'var(--bg-1)', color: 'var(--ink)',
                border: '1px solid var(--rule-2)',
                fontFamily: 'var(--font-mono)', fontSize: 12,
              }}
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          <Btn kind="ghost" onClick={() => triggerBackgroundRefresh(accounts)}>
            {refreshing ? '↻ Sync…' : '↻ Refresh'}
          </Btn>
        </>
      }
    >
      <div className="ta-filters">
        <div className="ta-tabs">
          <button className={`ta-tab ${selected === 'all' ? 'is-active' : ''}`} onClick={() => setSelected('all')}>
            Toutes
          </button>
          {accounts.map(a => (
            <button key={a.id} className={`ta-tab ${selected === a.id ? 'is-active' : ''}`} onClick={() => setSelected(a.id)}>
              {a.stationName || a.label || 'Station'}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="mono" style={{ padding: 32, color: 'var(--ink-3)' }}>
          Chargement des données…
        </div>
      )}

      {!loading && refreshing && (
        <div className="mono" style={{ padding: '12px 0', color: 'var(--accent)', fontSize: 11, letterSpacing: '0.14em' }}>
          ● Synchronisation Netatmo en cours…
        </div>
      )}

      {!loading && visibleStations.length === 0 && (
        <div className="mono" style={{ padding: 32, color: 'var(--ink-3)' }}>
          Aucune station connectée
        </div>
      )}

      {/* Station cards */}
      {!loading && visibleStations.length > 0 && (
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(visibleStations.length, 2)}, 1fr)`,
            gap: 0,
            border: '1px solid var(--rule)',
            marginBottom: 56,
          }}
          className="ta-rain-stations"
        >
          {visibleStations.map((s, i) => {
            const sm = computeSummary(s);
            return (
              <div
                key={s.account.id}
                style={{
                  padding: 32,
                  borderRight: i < visibleStations.length - 1 ? '1px solid var(--rule)' : 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span className="mono" style={{ color: colorFor(i), fontSize: 11, letterSpacing: '0.2em' }}>
                    ST-{String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="eyebrow" style={{ textTransform: 'none', letterSpacing: '0.14em' }}>
                    {s.account.stationName || s.account.label || 'Station'}
                  </span>
                </div>
                <div style={{ marginTop: 32, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                  <span className="mono tnum" style={{ fontSize: 72, fontWeight: 500, letterSpacing: '-0.04em', lineHeight: 0.9 }}>
                    {sm.total.toFixed(1)}
                  </span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    mm · cumul
                  </span>
                </div>
                <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, paddingTop: 20, borderTop: '1px solid var(--rule)' }}>
                  <div>
                    <div className="eyebrow">Max</div>
                    <div className="mono tnum" style={{ fontSize: 18, marginTop: 8 }}>{sm.max.toFixed(1)} mm</div>
                  </div>
                  <div>
                    <div className="eyebrow">Moyenne</div>
                    <div className="mono tnum" style={{ fontSize: 18, marginTop: 8 }}>{sm.average.toFixed(1)} mm</div>
                  </div>
                  <div>
                    <div className="eyebrow">Jours pluie</div>
                    <div className="mono tnum" style={{ fontSize: 18, marginTop: 8 }}>{sm.rainyDays}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Chart */}
      {combinedChart.length > 0 && combinedChart.some(c => c.values.some(v => v.v > 0)) && (
        <>
          <div className="eyebrow" style={{ marginBottom: 20 }}>§ 07.A · Historique · {period === 'week' ? '7 jours' : period === 'month' ? '30 jours' : `année ${selectedYear}`}</div>
          <section style={{ border: '1px solid var(--rule)', padding: 24 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${combinedChart.length}, 1fr)`,
              gap: 4,
              height: 200,
              alignItems: 'end',
            }}>
              {combinedChart.map((col, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, height: '100%', justifyContent: 'flex-end' }}>
                  <div style={{ display: 'flex', gap: 2, alignItems: 'end', width: '100%', height: '100%', justifyContent: 'center' }}>
                    {col.values.map((v, j) => {
                      const h = maxBarValue > 0 ? (v.v / maxBarValue) * 100 : 0;
                      return (
                        <div key={j} style={{
                          width: combinedChart.length > 20 ? 4 : 8,
                          minHeight: v.v > 0 ? 2 : 0,
                          height: `${Math.max(h, v.v > 0 ? 2 : 0)}%`,
                          background: colorFor(j),
                        }} />
                      );
                    })}
                  </div>
                  <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 50, transform: combinedChart.length > 12 ? 'rotate(-45deg)' : 'none', transformOrigin: 'top center' }}>
                    {col.label}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: combinedChart.length > 12 ? 32 : 20, paddingTop: 20, borderTop: '1px solid var(--rule)', display: 'flex', gap: 32, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.08em', flexWrap: 'wrap' }}>
              {visibleStations.map((s, i) => (
                <span key={s.account.id}>
                  <span style={{
                    display: 'inline-block', width: 10, height: 10, marginRight: 8, verticalAlign: 'middle',
                    background: colorFor(i),
                  }} />
                  {s.account.stationName || s.account.label || 'Station'}
                </span>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Empty chart fallback */}
      {!loading && combinedChart.length > 0 && !combinedChart.some(c => c.values.some(v => v.v > 0)) && (
        <div className="mono" style={{ padding: 32, color: 'var(--ink-3)', textAlign: 'center', border: '1px solid var(--rule)' }}>
          Aucune pluie sur la période · {refreshing ? 'Sync en cours…' : 'cache vide ou pas de précipitations'}
        </div>
      )}

      {/* Summary table */}
      {!loading && visibleStations.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: 56, marginBottom: 20 }}>§ 07.B · Cumuls</div>
          <section className="ta-rain-totals" style={{ border: '1px solid var(--rule)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div style={{ padding: 28, borderRight: '1px solid var(--rule)' }}>
              <div className="eyebrow">Total sélection</div>
              <div className="mono tnum" style={{ fontSize: 28, marginTop: 14, color: 'var(--accent)' }}>{totalSum.toFixed(1)} mm</div>
            </div>
            <div style={{ padding: 28, borderRight: '1px solid var(--rule)' }}>
              <div className="eyebrow">Jours de pluie</div>
              <div className="mono tnum" style={{ fontSize: 28, marginTop: 14 }}>{rainyDays}</div>
            </div>
            <div style={{ padding: 28 }}>
              <div className="eyebrow">Stations visibles</div>
              <div className="mono tnum" style={{ fontSize: 28, marginTop: 14 }}>{visibleStations.length}</div>
            </div>
          </section>
        </>
      )}

      <style>{`
        @media (max-width: 768px) {
          .ta-rain-stations { grid-template-columns: 1fr !important; }
          .ta-rain-stations > div { border-right: 0 !important; border-bottom: 1px solid var(--rule); }
          .ta-rain-stations > div:last-child { border-bottom: 0; }
          .ta-rain-totals { grid-template-columns: 1fr !important; }
          .ta-rain-totals > div { border-right: 0 !important; border-bottom: 1px solid var(--rule); }
          .ta-rain-totals > div:last-child { border-bottom: 0; }
        }
      `}</style>
    </PageShell>
  );
};

export default Rain;
